import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, Typography, Tab, Tabs, Chip, Paper, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress, Avatar, List, ListItem, ListItemText, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Scatter 
} from 'recharts';
import { 
  AttachMoney, People, TrendingUp, AccessTime, CheckCircle, SwapHoriz, 
  LocalActivity, Restaurant, Hotel, DirectionsCar, ThumbUp, ThumbDown, Speed, Psychology
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useThemeMode } from '../context/ThemeContext';

// --- DATOS MOCKADOS PARA REPORTES ---

// 1. P&L / Financiero
const pnlData = [
  { month: 'Ene', revenue: 45000, costs: 12000, profit: 33000, margin: 73 },
  { month: 'Feb', revenue: 48000, costs: 12500, profit: 35500, margin: 74 },
  { month: 'Mar', revenue: 52000, costs: 13000, profit: 39000, margin: 75 },
  { month: 'Abr', revenue: 51000, costs: 14000, profit: 37000, margin: 72 },
  { month: 'May', revenue: 58000, costs: 15000, profit: 43000, margin: 74 },
  { month: 'Jun', revenue: 65000, costs: 18000, profit: 47000, margin: 72 }, // Inicio verano
];

const serviceMixData = [
  { name: 'Spa & Wellness', value: 45, profit: 60 },
  { name: 'Restauración', value: 25, profit: 20 },
  { name: 'Tratamientos', value: 20, profit: 50 },
  { name: 'Retail', value: 10, profit: 30 },
];

// 2. Gestión / Operativo
const operationalData = [
  { hour: '08-10', visitors: 120, staff: 15, waitTime: 2 },
  { hour: '10-12', visitors: 450, staff: 25, waitTime: 8 },
  { hour: '12-14', visitors: 380, staff: 25, waitTime: 5 },
  { hour: '14-16', visitors: 200, staff: 20, waitTime: 3 },
  { hour: '16-18', visitors: 550, staff: 30, waitTime: 12 }, // Peak
  { hour: '18-20', visitors: 680, staff: 30, waitTime: 15 }, // Super Peak
  { hour: '20-22', visitors: 300, staff: 20, waitTime: 5 },
];

// 3. Métricas Modernas (NPS, CES, CSAT)
const cxData = [
  { metric: 'NPS (Net Promoter Score)', value: 72, trend: '+5', color: '#4caf50' },
  { metric: 'CES (Customer Effort)', value: 1.8, trend: '-0.2', color: '#2196f3' }, // Bajo es mejor
  { metric: 'CSAT (Satisfaction)', value: 4.8, trend: '+0.1', color: '#ff9800' },
];

const retentionCohort = [
  { cohort: 'Q1 2024', m1: 100, m2: 85, m3: 75, m6: 60, m12: 45 },
  { cohort: 'Q2 2024', m1: 100, m2: 88, m3: 78, m6: 65, m12: null }, // Más reciente
  { cohort: 'Q3 2024', m1: 100, m2: 90, m3: 82, m6: null, m12: null },
];

const internalServicesTracking = [
  { service: 'Mantenimiento Piscinas', uptime: 99.8, issues: 2, resolutionTime: '1.5h' },
  { service: 'Sistemas Acceso (Tornos)', uptime: 99.9, issues: 1, resolutionTime: '0.5h' },
  { service: 'Wifi Clientes', uptime: 98.5, issues: 8, resolutionTime: '2.0h' },
  { service: 'APP Reservas', uptime: 99.5, issues: 5, resolutionTime: '3.0h' },
];

// 4. BI Propietario PEpL: SmartTiming + Activos Caldea
const smartTimingKpis = [
  { title: 'Ventana Óptima', value: '18:10–19:40', subtext: '+14% apertura', icon: <AccessTime fontSize="large" />, color: '#00897b' },
  { title: 'Lift Redención', value: '+18%', subtext: '+6% vs control', icon: <SwapHoriz fontSize="large" />, color: '#1e88e5' },
  { title: 'CTR Rewards', value: '9.6%', subtext: '+2.4 pp', icon: <TrendingUp fontSize="large" />, color: '#7b1fa2' },
  { title: 'Activos OK', value: '96.4%', subtext: '+1.1% SLA', icon: <CheckCircle fontSize="large" />, color: '#43a047' },
];

const smartTimingSegments = [
  { segment: 'Socio Premium', bestWindow: '18:00–20:00', channel: 'Push + Email', openRate: 64, redemption: 22, asset: 'Caldea Thermal Premium' },
  { segment: 'Wellness Ocasional', bestWindow: '12:00–13:30', channel: 'Email', openRate: 42, redemption: 10, asset: 'Circuito Termal 3h' },
  { segment: 'Familias', bestWindow: '19:00–21:00', channel: 'Push', openRate: 58, redemption: 18, asset: 'Family Pack' },
  { segment: 'Turista Esquí', bestWindow: '16:30–18:00', channel: 'SMS + Push', openRate: 53, redemption: 15, asset: 'Après-Ski Relax' },
];

const assetIntelligence = [
  { asset: 'Piscina Exterior', utilization: 86, revenuePerHour: '€420/h', health: 'Óptimo', nextAction: 'Aumentar cupo +5%' },
  { asset: 'Circuito Termal', utilization: 92, revenuePerHour: '€560/h', health: 'Óptimo', nextAction: 'Mantener pricing' },
  { asset: 'Zona Kids', utilization: 64, revenuePerHour: '€210/h', health: 'Vigilar', nextAction: 'Campaña Family' },
  { asset: 'Restaurante Blu', utilization: 71, revenuePerHour: '€310/h', health: 'Óptimo', nextAction: 'Extender horario' },
];

const costKeyPreview = [
  { key: 'Costo base por servicio', value: '€6.80', note: 'Nominal uso + insumos estándar' },
  { key: 'Extra variable por reward', value: '€1.20', note: 'Upgrades, amenities, perks' },
  { key: 'Beneficio real estimado', value: '€9.40', note: 'Éxito x recursos / ventana post-reward' },
  { key: 'Margen real (preview)', value: '57.8%', note: 'Excluye mantenimiento & RRHH' },
];

// 5. SmartTiming segmentado por disponibilidad/costo-beneficio
const smartTimingSlots = [
  { resource: 'Spa', slot: '16:30–18:00', freeCap: 38, marginalCost: '€2.8', expectedRev: '€12.4', score: 86, segment: 'Premium', partner: 'Hotel' },
  { resource: 'Piscina', slot: '12:00–13:30', freeCap: 52, marginalCost: '€1.9', expectedRev: '€8.1', score: 78, segment: 'Familias', partner: 'N/A' },
  { resource: 'Circuito Termal', slot: '19:00–21:00', freeCap: 24, marginalCost: '€3.1', expectedRev: '€14.2', score: 90, segment: 'Wellness', partner: 'Caldea' },
  { resource: 'Restaurante', slot: '20:30–22:00', freeCap: 40, marginalCost: '€2.4', expectedRev: '€10.6', score: 82, segment: 'Turista', partner: 'Grandvalira' },
  { resource: 'Retail/Shop', slot: '17:00–18:30', freeCap: 60, marginalCost: '€1.2', expectedRev: '€6.5', score: 74, segment: 'Walk-in', partner: 'N/A' },
];

const smartTimingImprovements = [
  { title: 'Reducción de ociosidad', value: '-18%', detail: 'Slots ociosos convertidos en rewards con margen positivo.' },
  { title: 'Mayor redención', value: '+11%', detail: 'Segmentos con ventana óptima + canal correcto.' },
  { title: 'Costo marginal controlado', value: '-9%', detail: 'Asignación solo si el costo incremental es bajo.' },
  { title: 'Cross-sell aliado', value: '+6%', detail: 'Rewards coordinados con marcas en horarios de baja demanda.' },
];

const kpiDrilldown = [
  {
    title: 'Revenue Total',
    summary: '€299.5k · +12.5% vs last month',
    lines: [
      'Spa & Wellness: €134.8k (45%)',
      'Tratamientos: €74.9k (25%)',
      'Restauración: €59.9k (20%)',
      'Retail: €29.9k (10%)',
      'Ventana post‑reward aporta +7.2% del ingreso total',
    ],
    confidence: 'Cobertura eventos 92% · Última semana con sesgo estacional alto',
  },
  {
    title: 'Margen Operativo',
    summary: '73.8% · +2.1% efficiency gain',
    lines: [
      'Coste directo por servicio: 21.4% (insumos + consumibles)',
      'Coste variable por reward: 4.8% (perks + upgrades)',
      'Optimización PEpL (SmartTiming): +1.6 pp margen',
      'Mantenimiento/áreas comunes: no incluido (Key gestión en preview)',
    ],
    confidence: 'Metodología crítica con ventana post‑reward 7 días',
  },
  {
    title: 'NPS Global',
    summary: '72 · +5 points record high',
    lines: [
      'Promotores 78% · Pasivos 16% · Detractores 6%',
      'Top drivers: experiencia termal (+18), limpieza (+9)',
      'Drivers negativos: tiempos de espera (-6)',
      'Impacto PEpL en NPS: +3.2 pts (segmentos premium)',
    ],
    confidence: 'n=4,820 respuestas · 30 días',
  },
  {
    title: 'Retención Socios',
    summary: '89% · +4% via PEpL Rewards',
    lines: [
      'Retención M1: 92% · M3: 82% · M6: 65%',
      'Lift por SmartTiming: +6.1% (segmento Premium)',
      'Rewards con mayor impacto: Family Pack, Après‑Ski Relax',
      'Eventos post‑reward medidos: visitas, reservas, upsell',
    ],
    confidence: 'Cohortes Q1–Q3 2024 · tracking nominal',
  },
];

function StatCard({ title, value, subtext, icon, color }) {
  return (
    <Card sx={{ 
      borderRadius: 3, 
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
      '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: '0.8rem', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem', my: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: subtext.includes('+') ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
            {subtext}
          </Typography>
        </Box>
        <Avatar sx={{ 
          background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`, 
          color: color, 
          width: 48, 
          height: 48 
        }}>
          {icon}
        </Avatar>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('month');
  const { isDark, colors } = useThemeMode();
  
  // Theme-aware colors
  const themeColors = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    chipBg: isDark ? 'rgba(29,202,255,0.2)' : 'rgba(29,202,255,0.1)',
    tableBg: isDark ? '#0f172a' : '#f8fafc',
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: themeColors.textPrimary, fontSize: '1.75rem', mb: 0.5 }}>
            Business Intelligence
          </Typography>
          <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
            Algoritmo propietario SmartTiming™, gestión de activos Caldea y performance de negocio.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(e, newRange) => newRange && setTimeRange(newRange)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              px: 2,
              color: '#64748b',
              borderColor: 'rgba(0,0,0,0.08)',
              '&.Mui-selected': {
                bgcolor: '#0ea5e9',
                color: '#fff',
                '&:hover': { bgcolor: '#0284c7' }
              }
            }
          }}
        >
          <ToggleButton value="week">Semana</ToggleButton>
          <ToggleButton value="month">Mes</ToggleButton>
          <ToggleButton value="quarter">Trimestre</ToggleButton>
          <ToggleButton value="year">Año</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* KPI Cards Globales */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Revenue Total" 
            value="€299.5k" 
            subtext="+12.5% vs last month" 
            icon={<AttachMoney fontSize="medium" />} 
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Margen Operativo" 
            value="73.8%" 
            subtext="+2.1% efficiency gain" 
            icon={<TrendingUp fontSize="medium" />} 
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="NPS Global" 
            value="72" 
            subtext="+5 points record high" 
            icon={<ThumbUp fontSize="medium" />} 
            color="#f97316"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Retención Socios" 
            value="89%" 
            subtext="+4% via PEpL Rewards" 
            icon={<People fontSize="medium" />} 
            color="#a855f7"
          />
        </Grid>
      </Grid>

      {/* Drill-down de KPIs: claridad e integridad operativa */}
      <Card sx={{ 
        mb: 4,
        borderRadius: 3, 
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem', mb: 0.5 }}>
            Drill‑down de KPIs (Integridad Operativa PEpL)
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', mb: 2 }}>
            Desglose y trazabilidad del éxito del proceso PEpL: composición de métricas, fuentes y sesgos conocidos.
          </Typography>
          {kpiDrilldown.map((kpi) => (
            <Accordion 
              key={kpi.title} 
              disableGutters 
              elevation={0} 
              sx={{ 
                '&:before': { display: 'none' },
                bgcolor: 'transparent',
                '&.Mui-expanded': { bgcolor: '#fafbfc' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}> 
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{kpi.title}</Typography>
                  <Chip 
                    label={kpi.summary} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(14,165,233,0.1)', color: '#0284c7', fontWeight: 500, fontSize: '0.7rem' }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ py: 0 }}>
                  {kpi.lines.map((line) => (
                    <ListItem key={line} sx={{ py: 0.25, px: 0 }}>
                      <ListItemText primary={line} primaryTypographyProps={{ fontSize: '0.8rem', color: '#64748b' }} />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                  {kpi.confidence}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* BI Propietario PEpL: SmartTiming & Activos */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3, 
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem', mb: 0.5 }}>
                PEpL SmartTiming™ + Gestión de Activos Caldea
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', mb: 3 }}>
                Modelo propietario para estimar el momento óptimo de envío y apertura de rewards, con tracking de activos internos.
              </Typography>

              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#fafbfc', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Aclaración metodológica (derivado de servicios)
                </Typography>
                <List dense sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="Se computa como todos los eventos dentro de un rango posterior a la activación/aplicación del reward (ventana post-reward)." />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="Se basa en estadística de uso nominal y una metodología crítica para estimar el beneficio monetario real por reward, ponderando éxito y recursos consumidos." />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="En esta versión no se incluyen costos de mantenimiento, áreas comunes, extras o personal; se plantean como Key de gestión para próximas iteraciones." />
                  </ListItem>
                </List>
              </Paper>

              <Grid container spacing={3} sx={{ mb: 2 }}>
                {smartTimingKpis.map((kpi) => (
                  <Grid item xs={12} sm={6} md={3} key={kpi.title}>
                    <StatCard {...kpi} />
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>SmartTiming por Segmento</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Segmento</TableCell>
                          <TableCell>Ventana Óptima</TableCell>
                          <TableCell>Canal</TableCell>
                          <TableCell align="right">Open Rate</TableCell>
                          <TableCell align="right">Redención</TableCell>
                          <TableCell>Reward/Activo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {smartTimingSegments.map((row) => (
                          <TableRow key={row.segment}>
                            <TableCell><strong>{row.segment}</strong></TableCell>
                            <TableCell>{row.bestWindow}</TableCell>
                            <TableCell>{row.channel}</TableCell>
                            <TableCell align="right">{row.openRate}%</TableCell>
                            <TableCell align="right">{row.redemption}%</TableCell>
                            <TableCell>{row.asset}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Inteligencia de Activos Caldea</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Activo</TableCell>
                          <TableCell align="right">Utilización</TableCell>
                          <TableCell align="right">Ingreso/h</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assetIntelligence.map((row) => (
                          <TableRow key={row.asset}>
                            <TableCell><strong>{row.asset}</strong></TableCell>
                            <TableCell align="right">{row.utilization}%</TableCell>
                            <TableCell align="right">{row.revenuePerHour}</TableCell>
                            <TableCell>
                              <Chip 
                                label={row.health}
                                size="small"
                                color={row.health === 'Óptimo' ? 'success' : 'warning'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>SmartTiming por disponibilidad y costo‑beneficio</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                La lógica prioriza slots con capacidad libre y costo marginal bajo, maximizando el beneficio esperado. Si la agenda está llena, el reward no se activa.
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Recurso</TableCell>
                      <TableCell>Slot recomendado</TableCell>
                      <TableCell align="right">Capacidad libre</TableCell>
                      <TableCell align="right">Costo marginal</TableCell>
                      <TableCell align="right">Ingreso esperado</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell>Segmento</TableCell>
                      <TableCell>Partner</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {smartTimingSlots.map((row) => (
                      <TableRow key={`${row.resource}-${row.slot}`}>
                        <TableCell><strong>{row.resource}</strong></TableCell>
                        <TableCell>{row.slot}</TableCell>
                        <TableCell align="right">{row.freeCap}%</TableCell>
                        <TableCell align="right">{row.marginalCost}</TableCell>
                        <TableCell align="right">{row.expectedRev}</TableCell>
                        <TableCell align="right">
                          <Chip label={row.score} size="small" color={row.score >= 85 ? 'success' : row.score >= 75 ? 'warning' : 'default'} />
                        </TableCell>
                        <TableCell>{row.segment}</TableCell>
                        <TableCell>{row.partner}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {smartTimingImprovements.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.title}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="textSecondary">{item.title}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.value}</Typography>
                        <Typography variant="body2" color="textSecondary">{item.detail}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Key de Gestión: Coste Integral (preview)</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Key</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell>Notas</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {costKeyPreview.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell><strong>{row.key}</strong></TableCell>
                            <TableCell align="right">{row.value}</TableCell>
                            <TableCell>{row.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recomendación BI</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Incorporar costos de mantenimiento, extras y RRHH como clave de gestión para un margen real por reward y por activo.
                      </Typography>
                      <Chip label="Próxima fase: Cost-to-Serve" color="primary" size="small" />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs de Navegación */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered variant="fullWidth">
          <Tab icon={<Speed />} label="Operativo & Flujos" />
          <Tab icon={<Psychology />} label="Experiencia (CX) & Servicios" />
          <Tab icon={<AttachMoney />} label="Financiero (P&L)" />
        </Tabs>
      </Paper>

      {/* CONTENIDO TAB 0: OPERATIVO */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Gestión de Afluencia y Tiempos de Espera</Typography>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={operationalData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="hour" />
                      <YAxis yAxisId="left" label={{ value: 'Visitantes', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Minutos Espera', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="visitors" fill="#2196f3" stroke="#2196f3" fillOpacity={0.2} name="Afluencia (Pax)" />
                      <Line yAxisId="right" type="monotone" dataKey="waitTime" stroke="#f44336" strokeWidth={3} name="Tiempo Espera (min)" />
                      <Bar yAxisId="left" dataKey="staff" fill="#ff9800" name="Staff Activo" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" /> SLA Servicios Internos
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Servicio Crítico</TableCell>
                        <TableCell>Uptime Garantizado</TableCell>
                        <TableCell>Uptime Real</TableCell>
                        <TableCell>Incidencias (Mes)</TableCell>
                        <TableCell>Tiempo Medio Resolución</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {internalServicesTracking.map((row) => (
                        <TableRow key={row.service}>
                          <TableCell><strong>{row.service}</strong></TableCell>
                          <TableCell>99.0%</TableCell>
                          <TableCell sx={{ color: row.uptime >= 99 ? 'success.main' : 'warning.main', fontWeight: 'bold' }}>
                            {row.uptime}%
                          </TableCell>
                          <TableCell>{row.issues}</TableCell>
                          <TableCell>{row.resolutionTime}</TableCell>
                          <TableCell>
                            <Chip 
                              label={row.uptime >= 99 ? "Óptimo" : "Revisión"} 
                              color={row.uptime >= 99 ? "success" : "warning"} 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* CONTENIDO TAB 1: CX & EXPERIENCIA */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
           <Grid item xs={12} md={4}>
             <Card variant="outlined" sx={{ height: '100%' }}>
               <CardContent>
                 <Typography variant="h6" gutterBottom>Indicadores de Experiencia</Typography>
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                    {cxData.map((item) => (
                      <Box key={item.metric}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">{item.metric}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: item.color }}>{item.value}</Typography>
                            <Typography variant="caption" sx={{ color: item.trend.includes('+') ? 'success.main' : 'error.main' }}>
                              {item.trend}
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={item.metric === 'CES (Customer Effort)' ? (100 - (item.value*20)) : item.value} 
                          sx={{ 
                            height: 10, 
                            borderRadius: 5,
                            bgcolor: `${item.color}20`,
                            '& .MuiLinearProgress-bar': {
                              bgcolor: item.color
                            }
                          }} 
                        />
                      </Box>
                    ))}
                 </Box>
               </CardContent>
             </Card>
           </Grid>
           
           <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Análisis de Cohortes: Retención</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Porcentaje de socios que vuelven tras su primera visita (Mes 1 a Mes 12)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cohorte</TableCell>
                        <TableCell align="center">Mes 1</TableCell>
                        <TableCell align="center">Mes 2</TableCell>
                        <TableCell align="center">Mes 3</TableCell>
                        <TableCell align="center">Mes 6</TableCell>
                        <TableCell align="center">Mes 12</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retentionCohort.map((cohort) => (
                        <TableRow key={cohort.cohort}>
                          <TableCell component="th" scope="row">
                            <strong>{cohort.cohort}</strong>
                          </TableCell>
                          {['m1', 'm2', 'm3', 'm6', 'm12'].map((m) => (
                            <TableCell key={m} align="center" sx={{ 
                              bgcolor: cohort[m] ? `rgba(76, 175, 80, ${cohort[m]/150})` : 'transparent',
                              fontWeight: cohort[m] >= 80 ? 'bold' : 'normal'
                            }}>
                              {cohort[m] ? `${cohort[m]}%` : '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
           </Grid>
        </Grid>
      )}

      {/* CONTENIDO TAB 2: FINANCIERO */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Evolución de Ingresos vs Costos</Typography>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={pnlData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <RechartsTooltip />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="revenue" fill="#8884d8" stroke="#8884d8" name="Ingresos (€)" fillOpacity={0.3} />
                      <Bar yAxisId="left" dataKey="profit" barSize={20} fill="#413ea0" name="Beneficio (€)" />
                      <Line yAxisId="right" type="monotone" dataKey="margin" stroke="#ff7300" name="Margen (%)" strokeWidth={3} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Mix de Rentabilidad</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Contribución al beneficio por unidad de negocio.
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={serviceMixData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="profit" name="Margen Neto (%)" fill="#00C49F" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ mt: 2 }}>
                   <Chip label="Spa: High Volume" size="small" sx={{ mr: 1, bgcolor: '#e0f2f1', color: '#00695c' }} />
                   <Chip label="Tratamientos: High Margin" size="small" sx={{ bgcolor: '#fff3e0', color: '#ef6c00' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

    </Box>
  );
}
